// ============================================================
// API — talks to your EXISTING backend, unchanged.
// POST /api/video-call/:taskId/token
//   body: { callType, applicationId }
//   headers: Authorization: Bearer <idToken>
// Returns the same VideoCallTokenResult shape videoCall.controller.js
// already sends the RN app — for CALL_PROVIDER=webrtc that's:
//   { provider: 'webrtc', roomName, identity, name, applicationId,
//     stage, callType, iceServers, wsPath, ttlSeconds }
// (see webrtc.service.js's getCallAccessToken on the backend)
// ============================================================

// Plain words for the person; the server's technical App Check wording must
// never be shown. isAppCheckText() is also used by App.jsx / Whiteboard.jsx.
export const FRIENDLY_TRY_AGAIN = "We couldn't complete that just now. Please try again in a moment."
export const isAppCheckText = (text) => /app check/i.test(String(text ?? ''))

export async function fetchCallToken({ apiBaseUrl, taskId, callType, applicationId, idToken, appCheckToken }) {
  const headers = {
    'Content-Type':  'application/json',
    'Authorization': `Bearer ${idToken}`,
  }
  if (appCheckToken) headers['X-Firebase-AppCheck'] = appCheckToken

  const res = await fetch(`${apiBaseUrl}/api/video-call/${taskId}/token`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ callType, applicationId }),
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const appCheck = isAppCheckText(body.message)
    const err = new Error(appCheck ? FRIENDLY_TRY_AGAIN : (body.message || `Token request failed (${res.status})`))
    err.status = res.status
    err.body = body
    err.isAppCheck = appCheck
    throw err
  }

  const json = await res.json()
  // Matches response.util.js's success() envelope: { data, message }
  return json.data ?? json
}

// ============================================================
//  DIAGNOSTICS — added to actually pin down the whiteboard export
//  "Load failed" bug instead of guessing further. None of this
//  changes behavior on success; it only fires extra logging (and,
//  for the XHR fallback, an actual second network attempt) on the
//  failure paths we haven't been able to explain yet.
//
//  What each piece is for:
//   - dumpError(): captures every property an error might carry
//     (not just name/message) — WebKit errors sometimes attach
//     useful info in non-standard places.
//   - logEnvironmentOnce(): a snapshot of the WebView's networking-
//     relevant state (connection info, whether fetch/AbortController/
//     FormData are native or polyfilled, online status) taken once
//     per page load, so we can see if the failure correlates with a
//     particular network type or engine quirk.
//   - runCanaryRequest(): a plain unauthenticated GET to /health on
//     the SAME apiBaseUrl, run right before the real upload. If this
//     also fails the same way, the problem isn't specific to
//     POST/FormData/multipart at all — it's more fundamental
//     (DNS/TLS/ATS/the WebView's networking stack). If it succeeds
//     while the export POST still fails, that rules those out and
//     points back at something specific to the POST itself.
//   - xhrUploadFallback(): if fetch() throws, immediately retries
//     the IDENTICAL upload via XMLHttpRequest instead. XHR and
//     fetch use different code paths in WebKit for body streaming,
//     so if XHR succeeds where fetch fails, that's strong evidence
//     this is a fetch()-specific bug rather than a general
//     connectivity problem — and it also means the export actually
//     goes through instead of just failing twice.
// ============================================================

const isInRNWebView = () => typeof window !== 'undefined' && !!window.ReactNativeWebView

// FIX — THE "LOAD FAILED" DIAGNOSTICS NEVER REACH A REAL DEVICE LOG.
// Everything below (dumpError, the environment snapshot, the canary
// request, the XHR fallback result) was built specifically to pin down
// this export failure, and every bit of it goes out via console.log.
// WebCallView.tsx's console.log-forwarding bridge is deliberately
// __DEV__-only — see that file's own header: it used to forward every
// log line unconditionally, including signalling payloads and token
// state, which was a real security bug. That fix is correct and stays,
// but its side effect is that NONE of this instrumentation has ever
// reached a production device log. Every field report of "save
// failed" / "Load failed" has been undiagnosable without a developer
// physically attached via USB running a dev build — which is exactly
// the situation here: this has been happening in the field and there
// has been no way to see why.
//
// This sends a separate, narrow, ALWAYS-ON message — not the blanket
// console.log monkey-patch — carrying only structural network/error
// data: error name/message/stack, HTTP status, timings, the canary
// result, the XHR fallback result. No idToken, no appCheckToken, no
// signalling payload — none of that is ever part of these diagnostic
// objects to begin with. WebCallView.tsx logs it via console.error,
// which — like its existing 'js-error' handling — is already kept in
// every build because it's a genuine fault worth reporting, not
// routine chatter.
const reportExportDiagnostic = (payload) => {
  if (!isInRNWebView()) return
  try {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'wb-export-diagnostic', ...payload }))
  } catch { /* best-effort — reporting a failure must never itself throw */ }
}

const nowMs = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now())

const isNativeFn = (fn) => {
  try {
    return typeof fn === 'function' && /\[native code\]/.test(fn.toString())
  } catch {
    return 'unknown'
  }
}

const dumpError = (err) => {
  if (!err) return { value: err }
  const out = {
    name: err.name,
    message: err.message,
    stack: err.stack,
    constructorName: err.constructor?.name,
  }
  if (err.cause !== undefined) {
    out.cause = err.cause instanceof Error
      ? { name: err.cause.name, message: err.cause.message, stack: err.cause.stack }
      : err.cause
  }
  try {
    for (const key of Object.keys(err)) {
      if (!(key in out)) out[key] = err[key]
    }
  } catch { /* ignore — enumerating a hostile error object shouldn't crash logging */ }
  return out
}

let environmentLogged = false
const logEnvironmentOnce = () => {
  if (environmentLogged) return
  environmentLogged = true
  try {
    const conn = typeof navigator !== 'undefined' ? navigator.connection || navigator.webkitConnection || navigator.mozConnection : null
    console.log('[api] environment snapshot', {
      userAgent:   typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      onLine:      typeof navigator !== 'undefined' ? navigator.onLine : 'unknown',
      connection: conn ? {
        effectiveType: conn.effectiveType,
        downlink:      conn.downlink,
        rtt:           conn.rtt,
        saveData:      conn.saveData,
      } : 'unavailable',
      origin: typeof location !== 'undefined' ? location.origin : 'unknown',
      href:   typeof location !== 'undefined'
        ? location.href.replace(/idToken=[^&]+/, 'idToken=REDACTED')
        : 'unknown',
      fetchIsNative:        isNativeFn(typeof fetch !== 'undefined' ? fetch : undefined),
      hasAbortController:   typeof AbortController !== 'undefined',
      hasFormData:          typeof FormData !== 'undefined',
      hasBlob:              typeof Blob !== 'undefined',
      hasXHR:               typeof XMLHttpRequest !== 'undefined',
      inReactNativeWebView: typeof window !== 'undefined' && !!window.ReactNativeWebView,
      now: Date.now(),
    })
  } catch (err) {
    console.log('[api] environment snapshot FAILED', dumpError(err))
  }
}

// Cheap, unauthenticated, same-origin GET — run once per upload
// call, right before the real multipart POST. app.js exposes
// /health as one of publicLimiter's unauthenticated routes.
const runCanaryRequest = async (apiBaseUrl) => {
  const t0 = nowMs()
  try {
    const res = await fetch(`${apiBaseUrl}/health`, { method: 'GET' })
    console.log('[api] canary GET /health settled', {
      status: res.status, ok: res.ok, ms: Math.round(nowMs() - t0), now: Date.now(),
    })
    return { ok: res.ok, status: res.status }
  } catch (err) {
    console.log('[api] canary GET /health THREW', {
      ...dumpError(err), ms: Math.round(nowMs() - t0), now: Date.now(),
    })
    return { ok: false, error: dumpError(err) }
  }
}

// Identical upload, sent via XMLHttpRequest instead of fetch. Only
// invoked when fetch() has already thrown — this is both a
// diagnostic (does the OTHER request API behave differently?) and
// a real fallback (if it works, the export actually completes
// instead of failing twice).
const xhrUploadFallback = ({ apiBaseUrl, taskId, idToken, formData, appCheckToken }) =>
  new Promise((resolve) => {
    const t0 = nowMs()
    try {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', `${apiBaseUrl}/api/whiteboard/${taskId}/export`)
      xhr.setRequestHeader('Authorization', `Bearer ${idToken}`)
      if (appCheckToken) xhr.setRequestHeader('X-Firebase-AppCheck', appCheckToken)
      xhr.timeout = 20000

      xhr.onload = () => {
        console.log('[api] XHR fallback settled (load)', {
          status: xhr.status, ms: Math.round(nowMs() - t0), now: Date.now(),
        })
        let json = null
        try { json = JSON.parse(xhr.responseText) } catch { /* non-JSON body */ }
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, json })
      }
      xhr.onerror = () => {
        console.log('[api] XHR fallback settled (error event)', {
          status: xhr.status, readyState: xhr.readyState, ms: Math.round(nowMs() - t0), now: Date.now(),
        })
        resolve({ ok: false, viaErrorEvent: true, status: xhr.status })
      }
      xhr.ontimeout = () => {
        console.log('[api] XHR fallback TIMED OUT', { ms: Math.round(nowMs() - t0), now: Date.now() })
        resolve({ ok: false, viaTimeout: true })
      }
      xhr.onabort = () => {
        console.log('[api] XHR fallback ABORTED', { ms: Math.round(nowMs() - t0), now: Date.now() })
        resolve({ ok: false, viaAbort: true })
      }

      console.log('[api] XHR fallback sending', { now: Date.now() })
      xhr.send(formData)
    } catch (err) {
      console.log('[api] XHR fallback threw SYNCHRONOUSLY', {
        ...dumpError(err), ms: Math.round(nowMs() - t0), now: Date.now(),
      })
      resolve({ ok: false, threw: dumpError(err) })
    }
  })

// Materializes the PDF Blob as an ArrayBuffer-backed Blob before
// attaching it to FormData — see the header comment history for
// why (a prior fix targeting WebKit's synthetic-Blob serialization
// path; kept as cheap, harmless insurance even though it turned
// out not to be the actual root cause of the "Load failed" bug).
const toUploadableBlob = async (blob) => {
  const buffer = await blob.arrayBuffer()
  return new Blob([buffer], { type: blob.type || 'application/pdf' })
}

const isLoadFailedError = (err) =>
  err?.name === 'TypeError' && /load failed/i.test(err?.message || '')

const attemptUploadWhiteboardExport = async ({ apiBaseUrl, taskId, idToken, applicationId, blob, attempt, appCheckToken }) => {
  logEnvironmentOnce()

  const tStart = nowMs()

  let uploadableBlob
  try {
    uploadableBlob = await toUploadableBlob(blob)
    console.log('[api] blob->arrayBuffer conversion OK', {
      attempt, ms: Math.round(nowMs() - tStart), now: Date.now(),
    })
  } catch (err) {
    console.log('[api] blob->arrayBuffer conversion FAILED', {
      attempt, ...dumpError(err), ms: Math.round(nowMs() - tStart), now: Date.now(),
    })
    throw err
  }

  let formData
  try {
    formData = new FormData()
    formData.append('file', uploadableBlob, `whiteboard-${taskId}.pdf`)
    if (applicationId) formData.append('applicationId', applicationId)
    console.log('[api] FormData built OK', {
      attempt, ms: Math.round(nowMs() - tStart), now: Date.now(),
    })
  } catch (err) {
    console.log('[api] FormData construction FAILED', {
      attempt, ...dumpError(err), ms: Math.round(nowMs() - tStart), now: Date.now(),
    })
    throw err
  }

  console.log('[api] uploadWhiteboardExport starting', {
    attempt,
    visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
    hidden:          typeof document !== 'undefined' ? document.hidden : 'unknown',
    blobSize:        blob.size,
    uploadableSize:  uploadableBlob.size,
    now: Date.now(),
  })
  const visibilityHandler = () => {
    console.log('[api] visibilitychange during upload', {
      attempt, visibilityState: document.visibilityState, hidden: document.hidden, now: Date.now(),
    })
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', visibilityHandler)
  }

  // Canary — only on the first attempt, so a retry doesn't spend an
  // extra request re-confirming what attempt 1 already told us.
  if (attempt === 1) {
    await runCanaryRequest(apiBaseUrl)
  }

  // Timeout via Promise.race, NOT AbortController — see prior fix
  // notes: AbortSignal + FormData/Blob body is a known WebKit
  // combination that can fail immediately, independent of network
  // conditions. fetch() here never receives a signal.
  let timeoutId
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      console.log('[api] upload timeout FIRED (request left running in background)', { attempt, now: Date.now() })
      const timeoutErr = new Error('Upload timed out — check your connection and try again.')
      timeoutErr.status = 0
      timeoutErr.isTimeout = true
      reject(timeoutErr)
    }, 45_000)
  })

  const tFetchStart = nowMs()
  let res
  try {
    console.log('[api] calling fetch() now', { attempt, hasAppCheckToken: !!appCheckToken, now: Date.now() })
    // BUG FIX — 401 "Unauthorized — missing App Check token": this
    // endpoint is not on appCheck.middleware.js's exempt list, and
    // APP_CHECK_ENFORCED is true on staging. Server logs confirmed
    // '[appCheck] BLOCKED — missing token' firing for every one of
    // these requests once the earlier CORS bug stopped masking it.
    // getAppCheckToken() only exists in native RN code
    // (@react-native-firebase/app-check) — this WebView is a
    // sandboxed web page with no access to that SDK, so the native
    // side must fetch the token and pass it in as a bootstrap param,
    // the same way idToken already is (see main.jsx). If it's
    // missing here, the request still goes out — the backend, not
    // this file, decides whether a missing token is fatal.
    const headers = { 'Authorization': `Bearer ${idToken}` }
    if (appCheckToken) headers['X-Firebase-AppCheck'] = appCheckToken

    res = await Promise.race([
      fetch(`${apiBaseUrl}/api/whiteboard/${taskId}/export`, {
        method: 'POST',
        headers,
        body: formData,
      }),
      timeoutPromise,
    ])
    console.log('[api] fetch settled (response received)', {
      attempt, status: res.status, ms: Math.round(nowMs() - tFetchStart), now: Date.now(),
    })
  } catch (err) {
    console.log('[api] fetch settled (threw)', {
      attempt, ...dumpError(err), ms: Math.round(nowMs() - tFetchStart), now: Date.now(),
    })

    // Only chase the XHR fallback for the specific unexplained
    // "Load failed" case — a real timeout or a clean network error
    // (offline, DNS failure with a distinct message) doesn't need
    // it, and we don't want to double-submit the export on every
    // possible failure type.
    if (isLoadFailedError(err) && typeof XMLHttpRequest !== 'undefined') {
      console.log('[api] attempting XHR fallback after fetch Load failed', { attempt, now: Date.now() })
      const xhrResult = await xhrUploadFallback({ apiBaseUrl, taskId, idToken, formData, appCheckToken })
      if (xhrResult.ok) {
        console.log('[api] XHR fallback SUCCEEDED where fetch failed', { attempt, status: xhrResult.status, now: Date.now() })
        clearTimeout(timeoutId)
        if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', visibilityHandler)
        return xhrResult.json?.data ?? xhrResult.json
      }
      console.log('[api] XHR fallback ALSO failed', { attempt, xhrResult, now: Date.now() })
      err.xhrFallbackResult = xhrResult
    }

    // See reportExportDiagnostic's header note — this is the actual
    // "Load failed" failure point, and the one that has never before
    // been visible outside a dev build attached via USB.
    reportExportDiagnostic({
      stage: 'fetch', attempt, taskId,
      error: dumpError(err),
      xhrFallbackAttempted: !!err.xhrFallbackResult,
      xhrFallbackResult: err.xhrFallbackResult || null,
      ms: Math.round(nowMs() - tFetchStart),
    })

    throw err
  } finally {
    clearTimeout(timeoutId)
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', visibilityHandler)
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    console.error('[api] uploadWhiteboardExport failed', { attempt, status: res.status, body })
    const appCheck = isAppCheckText(body.message)
    const err = new Error(appCheck ? FRIENDLY_TRY_AGAIN : (body.message || `Export failed (${res.status})`))
    err.status = res.status
    err.body = body
    err.isAppCheck = appCheck
    // A non-2xx response is a real server answer, not a "Load failed"
    // network mystery — still worth having in the field, since it's
    // the difference between "the server rejected this" (401, 413, a
    // bad PDF) and the unexplained transport failure the rest of this
    // file exists to chase.
    reportExportDiagnostic({ stage: 'http-status', attempt, taskId, status: res.status, body })
    throw err
  }

  const json = await res.json()
  return json.data ?? json
}

// ── RN bridge upload ──────────────────────────────────────────
//
// FIX — WEBVIEW BLOB UPLOAD BLOCKED ON IOS (WKWebView):
//
// iOS WKWebView cannot upload a multipart/form-data body containing
// a Blob or binary data via fetch() or XMLHttpRequest when running
// inside a React Native WebView. Both calls fail immediately with
// TypeError: Load failed (status 0) before the request ever leaves
// the process — confirmed by the canary GET /health succeeding on
// the same network connection right before the POST fails.
//
// Root cause: WKWebView's network sandbox blocks binary body
// streaming for WebView-hosted pages in a React Native app. This is
// a known iOS limitation, not a server or CORS problem.
//
// Fix: when running inside the RN WebView (window.ReactNativeWebView
// is present), base64-encode the PDF blob and post it as a JSON
// message to the native side over the existing bridge. The native
// side decodes it and uploads it using React Native's own fetch —
// which runs outside WKWebView's network sandbox and has no such
// restriction.
//
// The native message type is 'wb-export-upload'. WebCallView.tsx
// handles it, does the real upload, and posts back:
//   { type: 'wb-export-upload-result', ok: true,  data: {...} }
//   { type: 'wb-export-upload-result', ok: false, error: string }
//
// This path is only used when inside the RN WebView. In a plain
// browser the direct fetch path below still runs.

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      // result is "data:application/pdf;base64,<data>" — strip prefix
      const result = reader.result
      const comma = result.indexOf(',')
      resolve(comma !== -1 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })

const uploadViaNativeBridge = ({ taskId, applicationId, blob, idToken, appCheckToken }) =>
  new Promise(async (resolve, reject) => {
    const t0 = nowMs()
    let base64
    try {
      base64 = await blobToBase64(blob)
      console.log('[api] bridge: blob->base64 OK', { size: base64.length, now: Date.now() })
    } catch (err) {
      console.log('[api] bridge: blob->base64 FAILED', dumpError(err))
      reject(err)
      return
    }

    // One-time listener — resolves/rejects when the native side
    // responds with the upload result.
    const handler = (event) => {
      let msg
      try { msg = JSON.parse(event.nativeEvent?.data ?? event.data) } catch { return }
      if (msg?.type !== 'wb-export-upload-result') return
      window.removeEventListener('message', handler)
      const ms = Math.round(nowMs() - t0)
      if (msg.ok) {
        console.log('[api] bridge upload SUCCEEDED', { ms, now: Date.now() })
        resolve(msg.data)
      } else {
        console.log('[api] bridge upload FAILED', { error: msg.error, ms, now: Date.now() })
        const appCheck = isAppCheckText(msg.error)
        const err = new Error(appCheck ? FRIENDLY_TRY_AGAIN : (msg.error || 'Upload failed via native bridge'))
        err.viaBridge = true
        err.isAppCheck = appCheck
        reject(err)
      }
    }
    // RN WebView dispatches messages as 'message' events on window.
    window.addEventListener('message', handler)

    console.log('[api] posting wb-export-upload to native bridge', {
      taskId, applicationId: applicationId ?? null, blobSize: blob.size, now: Date.now(),
    })
    window.ReactNativeWebView.postMessage(JSON.stringify({
      type:          'wb-export-upload',
      taskId,
      applicationId: applicationId ?? null,
      base64Pdf:     base64,
      // idToken and appCheckToken are passed so the native side can
      // authenticate the request with the same credentials it already
      // holds — they were handed in as URL params and are valid for
      // the call's lifetime.
      idToken,
      appCheckToken: appCheckToken ?? null,
    }))

    // Safety timeout — if the native side never responds (e.g. it
    // crashed or the bridge message handler was never registered),
    // don't hang forever.
    setTimeout(() => {
      window.removeEventListener('message', handler)
      const err = new Error('Native bridge upload timed out after 45s')
      err.viaBridge = true
      err.isTimeout = true
      reject(err)
    }, 45_000)
  })

// PUBLIC ENTRY POINT
//
// FIX — iOS WKWebView blocks binary FormData uploads (see above):
// When inside the RN WebView, route through the native bridge
// instead of fetch/XHR. Outside RN (plain browser, tests, etc.)
// the original direct-fetch path still runs.
export async function uploadWhiteboardExport(params) {
  if (isInRNWebView()) {
    return uploadViaNativeBridge(params)
  }
  // Non-RN path: direct fetch with one retry on "Load failed"
  try {
    return await attemptUploadWhiteboardExport({ ...params, attempt: 1 })
  } catch (err) {
    if (!isLoadFailedError(err)) throw err
    console.log('[api] uploadWhiteboardExport retrying once after "Load failed"', { now: Date.now() })
    return await attemptUploadWhiteboardExport({ ...params, attempt: 2 })
  }
}

export function wsUrlFromApiBase(apiBaseUrl, path) {
  return `${apiBaseUrl.replace(/^http/, 'ws')}${path}`
}